/**
 * Environment Configuration
 * Centralized configuration for API keys and environment variables
 */

import { Platform } from 'react-native';

export const ENV_CONFIG = {
  // Text-to-Speech API Configuration
  TTS: {
    GOOGLE_CLOUD_API_KEY: process.env.EXPO_PUBLIC_GOOGLE_TTS_API_KEY || '',
    AWS_ACCESS_KEY_ID: process.env.EXPO_PUBLIC_AWS_ACCESS_KEY_ID || '',
    AWS_SECRET_ACCESS_KEY: process.env.EXPO_PUBLIC_AWS_SECRET_ACCESS_KEY || '',
    AWS_REGION: process.env.EXPO_PUBLIC_AWS_REGION || 'us-east-1',
  },
  
  // CDN Configuration
  CDN: {
    AUDIO_BASE_URL: process.env.EXPO_PUBLIC_AUDIO_CDN_URL || 'https://cdn.kingdomquiz.com/audio',
    CONTENT_BASE_URL: process.env.EXPO_PUBLIC_CONTENT_CDN_URL || 'https://cdn.kingdomquiz.com/content',
  },
  
  // Monetization Configuration
  MONETIZATION: {
    ENABLED: process.env.EXPO_PUBLIC_MONETIZATION_ENABLED === 'true',
    SUBSCRIPTION_ENABLED: process.env.EXPO_PUBLIC_SUBSCRIPTION_ENABLED === 'true',
    MARKETPLACE_ENABLED: process.env.EXPO_PUBLIC_MARKETPLACE_ENABLED === 'true',
    IN_APP_PURCHASES_ENABLED: process.env.EXPO_PUBLIC_IAP_ENABLED === 'true',
  },
  
  // Payment Provider Configuration
  PAYMENT: {
    STRIPE_PUBLIC_KEY: process.env.EXPO_PUBLIC_STRIPE_PUBLIC_KEY || '',
    STRIPE_WEBHOOK_SECRET: process.env.STRIPE_WEBHOOK_SECRET || '',
    APPLE_IAP_ENABLED: process.env.EXPO_PUBLIC_APPLE_IAP_ENABLED === 'true',
    GOOGLE_IAP_ENABLED: process.env.EXPO_PUBLIC_GOOGLE_IAP_ENABLED === 'true',
  },

  // RevenueCat Configuration
  REVENUECAT: {
    IOS_KEY: process.env.EXPO_PUBLIC_REVENUECAT_IOS_KEY || '',
    ANDROID_KEY: process.env.EXPO_PUBLIC_REVENUECAT_ANDROID_KEY || '',
    DEBUG_MODE: process.env.EXPO_PUBLIC_REVENUECAT_DEBUG === 'true',
  },
  
  // Subscription Tiers and Pricing
  SUBSCRIPTION_TIERS: {
    INDIVIDUAL: {
      MONTHLY: process.env.EXPO_PUBLIC_INDIVIDUAL_MONTHLY_PRICE || '4.99',
      ANNUAL: process.env.EXPO_PUBLIC_INDIVIDUAL_ANNUAL_PRICE || '49.99',
      STRIPE_MONTHLY_PRICE_ID: process.env.EXPO_PUBLIC_INDIVIDUAL_MONTHLY_STRIPE_ID || '',
      STRIPE_ANNUAL_PRICE_ID: process.env.EXPO_PUBLIC_INDIVIDUAL_ANNUAL_STRIPE_ID || '',
    },
    FAMILY: {
      MONTHLY: process.env.EXPO_PUBLIC_FAMILY_MONTHLY_PRICE || '4.99',
      ANNUAL: process.env.EXPO_PUBLIC_FAMILY_ANNUAL_PRICE || '39.99',
      STRIPE_MONTHLY_PRICE_ID: process.env.EXPO_PUBLIC_FAMILY_MONTHLY_STRIPE_ID || '',
      STRIPE_ANNUAL_PRICE_ID: process.env.EXPO_PUBLIC_FAMILY_ANNUAL_STRIPE_ID || '',
    },
    GROUP_SMALL: {
      MONTHLY: process.env.EXPO_PUBLIC_GROUP_SMALL_MONTHLY_PRICE || '19.99',
      ANNUAL: process.env.EXPO_PUBLIC_GROUP_SMALL_ANNUAL_PRICE || '199.99',
      STRIPE_MONTHLY_PRICE_ID: process.env.EXPO_PUBLIC_GROUP_SMALL_MONTHLY_STRIPE_ID || '',
      STRIPE_ANNUAL_PRICE_ID: process.env.EXPO_PUBLIC_GROUP_SMALL_ANNUAL_STRIPE_ID || '',
    },
    GROUP_MEDIUM: {
      MONTHLY: process.env.EXPO_PUBLIC_GROUP_MEDIUM_MONTHLY_PRICE || '49.99',
      ANNUAL: process.env.EXPO_PUBLIC_GROUP_MEDIUM_ANNUAL_PRICE || '499.99',
      STRIPE_MONTHLY_PRICE_ID: process.env.EXPO_PUBLIC_GROUP_MEDIUM_MONTHLY_STRIPE_ID || '',
      STRIPE_ANNUAL_PRICE_ID: process.env.EXPO_PUBLIC_GROUP_MEDIUM_ANNUAL_STRIPE_ID || '',
    },
    GROUP_LARGE: {
      MONTHLY: process.env.EXPO_PUBLIC_GROUP_LARGE_MONTHLY_PRICE || '99.99',
      ANNUAL: process.env.EXPO_PUBLIC_GROUP_LARGE_ANNUAL_PRICE || '999.99',
      STRIPE_MONTHLY_PRICE_ID: process.env.EXPO_PUBLIC_GROUP_LARGE_MONTHLY_STRIPE_ID || '',
      STRIPE_ANNUAL_PRICE_ID: process.env.EXPO_PUBLIC_GROUP_LARGE_ANNUAL_STRIPE_ID || '',
    },
  },
  
  // Feature Flags
  FEATURES: {
    AUDIO_ENABLED: false, // Temporarily disabled
    COMMUNITY_TRANSLATIONS: process.env.EXPO_PUBLIC_COMMUNITY_TRANSLATIONS !== 'false',
    RTL_SUPPORT: process.env.EXPO_PUBLIC_RTL_SUPPORT !== 'false',
    ADMIN_DASHBOARD: process.env.EXPO_PUBLIC_ADMIN_DASHBOARD !== 'false',
    
    // Monetization feature flags
    MONETIZATION_ENABLED: process.env.EXPO_PUBLIC_MONETIZATION_ENABLED === 'true',
    PREMIUM_AUDIO_ENABLED: process.env.EXPO_PUBLIC_PREMIUM_AUDIO_ENABLED === 'true',
    PREMIUM_QUIZ_PACKS_ENABLED: process.env.EXPO_PUBLIC_PREMIUM_QUIZ_PACKS_ENABLED === 'true',
    GROUP_FEATURES_ENABLED: process.env.EXPO_PUBLIC_GROUP_FEATURES_ENABLED === 'true',
    CREATOR_MARKETPLACE_ENABLED: process.env.EXPO_PUBLIC_CREATOR_MARKETPLACE_ENABLED === 'true',
  },
  
  // Development Configuration
  DEV: {
    ENABLE_MOCK_TTS: process.env.EXPO_PUBLIC_MOCK_TTS === 'true',
    ENABLE_DEBUG_LOGS: process.env.EXPO_PUBLIC_DEBUG_LOGS === 'true',
    ENABLE_MOCK_PAYMENTS: process.env.EXPO_PUBLIC_MOCK_PAYMENTS === 'true',
  }
};

// Helper functions
export const isFeatureEnabled = (feature: keyof typeof ENV_CONFIG.FEATURES): boolean => {
  return ENV_CONFIG.FEATURES[feature];
};

export const isMonetizationEnabled = (): boolean => {
  return ENV_CONFIG.MONETIZATION.ENABLED;
};

export const isSubscriptionEnabled = (): boolean => {
  return ENV_CONFIG.MONETIZATION.SUBSCRIPTION_ENABLED;
};

export const isMarketplaceEnabled = (): boolean => {
  return ENV_CONFIG.MONETIZATION.MARKETPLACE_ENABLED;
};

export const getTTSConfig = () => {
  return ENV_CONFIG.TTS;
};

export const getCDNConfig = () => {
  return ENV_CONFIG.CDN;
};

export const getPaymentConfig = () => {
  return ENV_CONFIG.PAYMENT;
};

export const getSubscriptionTiers = () => {
  return ENV_CONFIG.SUBSCRIPTION_TIERS;
};

// Validation function to check if required environment variables are set
export const validateEnvironment = (): { valid: boolean; missing: string[] } => {
  const missing: string[] = [];
  
  // Check TTS configuration if audio is enabled
  if (isFeatureEnabled('AUDIO_ENABLED')) {
    if (!ENV_CONFIG.TTS.GOOGLE_CLOUD_API_KEY && !ENV_CONFIG.TTS.AWS_ACCESS_KEY_ID) {
      missing.push('TTS API keys (Google Cloud or AWS)');
    }
  }
  
  // Check monetization configuration if enabled
  // Stripe is only used on web — mobile platforms use RevenueCat for native billing.
  if (isMonetizationEnabled() && Platform.OS === 'web') {
    if (isSubscriptionEnabled() && !ENV_CONFIG.PAYMENT.STRIPE_PUBLIC_KEY) {
      missing.push('Stripe public key for subscription processing');
    }

    // Only the FAMILY tier maps to the active Premium plan
    const familyTier = ENV_CONFIG.SUBSCRIPTION_TIERS.FAMILY;
    if (!familyTier.STRIPE_MONTHLY_PRICE_ID || !familyTier.STRIPE_ANNUAL_PRICE_ID) {
      missing.push('Stripe price IDs for Premium (Family) subscription tier');
    }
  }
  
  return {
    valid: missing.length === 0,
    missing
  };
};
